import { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { Mail, Paperclip, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InboxAnalysis } from "@/components/inbox/InboxAnalysis";

const fmt = d => { try { return format(parseISO(d), "d MMM yyyy HH:mm"); } catch { return d || ""; } };
const STATUS_LABELS = { new: "New", read: "Read", actioned: "Actioned", archived: "Archived" };
const STATUS_CLS = { new: "bg-[#83B68B] text-black", read: "bg-white/10", actioned: "bg-sky-500/20 text-sky-300", archived: "bg-white/5 opacity-60" };

export function InboxTab() {
  const [items, setItems] = useState(null);
  const [config, setConfig] = useState(null);
  const [statusF, setStatusF] = useState("all");
  const [open, setOpen] = useState(null);
  const [logOpen, setLogOpen] = useState(false);
  const load = () => api.get("/admin/inbox", { params: { status: statusF } }).then(r => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, [statusF]); // eslint-disable-line
  useEffect(() => { api.get("/admin/inbox/config").then(r => setConfig(r.data)).catch(() => {}); }, []);

  return (
    <div className="space-y-4" data-testid="admin-inbox-tab">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Records</div>
          <h2 className="font-display font-black text-3xl mt-1">Club Inbox</h2>
          <p className="opacity-60 text-sm mt-1">Emails forwarded to the club address land here with their attachments. Mark them actioned when dealt with.</p>
        </div>
        <Button onClick={() => setLogOpen(true)} data-testid="inbox-log-manual-btn"><Plus className="w-4 h-4 mr-1" />Log email manually</Button>
      </div>
      {config && <ConfigBanner config={config} />}
      <div className="flex gap-2">
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="w-44" data-testid="inbox-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {items === null ? <div className="opacity-60 text-sm">Loading…</div>
        : items.length === 0 ? <div className="opacity-60 text-sm border border-dashed border-border rounded-xl p-8 text-center" data-testid="inbox-empty">No emails yet.</div>
        : <div className="space-y-2" data-testid="inbox-list">{items.map(m => <InboxRow key={m.id} m={m} onOpen={() => setOpen(m.id)} />)}</div>}
      {open && <InboxDetail id={open} onClose={() => { setOpen(null); load(); }} />}
      {logOpen && <ManualLogDialog onClose={() => setLogOpen(false)} onSaved={() => { setLogOpen(false); load(); }} />}
    </div>
  );
}

function ConfigBanner({ config }) {
  if (config.configured) return (
    <div className="text-xs rounded-lg border border-[#83B68B]/40 bg-[#83B68B]/10 p-3" data-testid="inbox-config-ok">
      Receiving live{config.address ? <> at <span className="font-mono">{config.address}</span></> : null}. Forward club emails there and they appear below automatically.
    </div>
  );
  return (
    <div className="text-xs rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-1" data-testid="inbox-config-missing">
      <div className="font-semibold">Automatic receiving not connected yet.</div>
      <div className="opacity-80">Add <span className="font-mono">RESEND_API_KEY</span>, <span className="font-mono">RESEND_WEBHOOK_SECRET</span> and <span className="font-mono">INBOUND_ADDRESS</span> to the backend, verify a receiving domain in Resend and point its <span className="font-mono">email.received</span> webhook at <span className="font-mono">{config.webhook_path}</span>. Until then, use “Log email manually”.</div>
    </div>
  );
}

function InboxRow({ m, onOpen }) {
  return (
    <button onClick={onOpen} className="w-full text-left rounded-xl border border-border bg-card p-3 hover:border-[#83B68B]/60 transition-colors" data-testid={`inbox-row-${m.id}`}>
      <div className="flex items-start gap-3">
        <Mail className="w-4 h-4 mt-1 opacity-60 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider ${STATUS_CLS[m.status]}`}>{STATUS_LABELS[m.status]}</span>
            <span className="font-semibold truncate">{m.subject}</span>
            {m.analysis?.type && <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border border-[#83B68B]/50 text-[#83B68B]" data-testid={`inbox-row-type-${m.id}`}>{m.analysis.type.replace("_", " ")}</span>}
            {m.attachments?.length > 0 && <span className="text-xs opacity-60 flex items-center gap-1"><Paperclip className="w-3 h-3" />{m.attachments.length}</span>}
          </div>
          <div className="text-xs opacity-60 mt-1 truncate">{m.from_addr} · {fmt(m.received_at)}{m.source === "manual" && " · logged manually"}</div>
        </div>
      </div>
    </button>
  );
}

function InboxDetail({ id, onClose }) {
  const [m, setM] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => { api.get(`/admin/inbox/${id}`).then(r => { setM(r.data); setNote(r.data.note || ""); }).catch(e => { toast.error(formatApiError(e)); onClose(); }); }, [id]); // eslint-disable-line
  const setStatus = status => api.patch(`/admin/inbox/${id}`, { status, note }).then(r => { setM(x => ({ ...x, ...r.data })); toast.success(`Marked ${STATUS_LABELS[status]}`); }).catch(e => toast.error(formatApiError(e)));
  const del = () => { if (!window.confirm("Delete this email?")) return; api.delete(`/admin/inbox/${id}`).then(() => { toast.success("Deleted"); onClose(); }).catch(e => toast.error(formatApiError(e))); };
  const token = localStorage.getItem("access_token");
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="inbox-detail-dialog" aria-describedby={undefined}>
        {!m && <DialogHeader><DialogTitle>Loading…</DialogTitle></DialogHeader>}
        {m && <>
          <DialogHeader><DialogTitle>{m.subject}</DialogTitle></DialogHeader>
          <div className="text-xs opacity-60">From {m.from_addr} · {fmt(m.received_at)}</div>
          <pre className="whitespace-pre-wrap text-sm font-sans bg-black/20 rounded-lg p-3 max-h-80 overflow-y-auto" data-testid="inbox-detail-body">{m.text || "(no text body)"}</pre>
          <InboxAnalysis item={m} onChanged={() => api.get(`/admin/inbox/${id}`).then(r => setM(r.data))} />
          {m.attachments?.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] tracking-widest uppercase opacity-60">Attachments</div>
              {m.attachments.map((a, i) => a.url
                ? <a key={i} href={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#83B68B] hover:underline" data-testid={`inbox-attachment-${i}`}><Paperclip className="w-3 h-3" />{a.filename} <span className="opacity-50 text-xs">{a.size ? `${Math.round(a.size / 1024)} KB` : ""}</span></a>
                : <div key={i} className="text-sm opacity-60 flex items-center gap-2"><Paperclip className="w-3 h-3" />{a.filename} — {a.skipped || a.error}</div>)}
            </div>
          )}
          <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Internal note (what was done)" rows={2} data-testid="inbox-note-input" />
          <div className="flex flex-wrap gap-2 justify-between">
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setStatus("actioned")} data-testid="inbox-mark-actioned">Mark actioned</Button>
              <Button size="sm" variant="secondary" onClick={() => setStatus("archived")} data-testid="inbox-mark-archived">Archive</Button>
              <Button size="sm" variant="ghost" onClick={() => setStatus("read")} data-testid="inbox-save-note">Save note</Button>
            </div>
            <Button size="sm" variant="ghost" className="text-red-400" onClick={del} data-testid="inbox-delete"><Trash2 className="w-4 h-4" /></Button>
          </div>
        </>}
      </DialogContent>
    </Dialog>
  );
}

function ManualLogDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ from_addr: "", subject: "", text: "" });
  const [busy, setBusy] = useState(false);
  const save = () => {
    setBusy(true);
    api.post("/admin/inbox/manual", f).then(() => { toast.success("Email logged"); onSaved(); }).catch(e => toast.error(formatApiError(e))).finally(() => setBusy(false));
  };
  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="inbox-manual-dialog">
        <DialogHeader><DialogTitle>Log an email manually</DialogTitle></DialogHeader>
        <Input placeholder="From (email address)" value={f.from_addr} onChange={e => setF({ ...f, from_addr: e.target.value })} data-testid="inbox-manual-from" />
        <Input placeholder="Subject" value={f.subject} onChange={e => setF({ ...f, subject: e.target.value })} data-testid="inbox-manual-subject" />
        <Textarea placeholder="Paste the email body" rows={8} value={f.text} onChange={e => setF({ ...f, text: e.target.value })} data-testid="inbox-manual-text" />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || f.from_addr.length < 3 || f.text.length < 1} data-testid="inbox-manual-save">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
